import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Network, Building2, MessageSquare, Gift, Clock, Lock } from 'lucide-react';
import { Navbar } from './landing/Navbar';
import { Footer } from './landing/Footer';
import { RoadmapSection } from './landing/RoadmapFAQSections';
import { DemoRequestModal } from './landing/DemoRequestModal';

const FUTURE_FEATURES = [
    {
        icon: Network,
        title: "Homly Réseau",
        eta: "Fin 2026",
        status: "planned",
        description: "Plateforme inter-agences qui connecte les vendeurs aux meilleurs agents de leur zone. Votre profil public, vos avis, vos résultats et votre réactivité deviennent votre meilleure publicité. Visibilité maximale dans les recherches locales de vendeurs.",
        bullets: [
            "Profil agence public avec avis vérifiés",
            "Matching vendeur ↔ agence basé sur la zone et la spécialité",
            "Notation transparente sur chaque mandat",
        ],
    },
    {
        icon: Building2,
        title: "Dashboard Groupe / Multi-agences",
        eta: "2027",
        status: "planned",
        description: "Vue consolidée pour les réseaux et franchises : pilotage de plusieurs agences depuis un seul cockpit, benchmark inter-agences, KPI groupés, alertes consolidées.",
        bullets: [
            "Cockpit groupe avec drill-down par agence",
            "Benchmark anonymisé inter-agences",
            "Reporting consolidé multi-régions",
        ],
    },
    {
        icon: MessageSquare,
        title: "Invitations vendeurs par SMS",
        eta: "2027",
        status: "planned",
        description: "Onboarding des vendeurs par SMS pour maximiser le taux d'activation de l'espace vendeur. Lien direct, sans création de compte, avec authentification simplifiée.",
        bullets: [
            "Envoi SMS direct (intégration Twilio)",
            "Lien vendeur magique, sans password",
            "Notifications visites + offres en temps réel",
        ],
    },
    {
        icon: Gift,
        title: "Programme Ambassadeur",
        eta: "2027",
        status: "planned",
        description: "Système de parrainage entre agences. Chaque agence Homly recommande, chaque parrainage déclenche une récompense (crédit Homly ou mois offerts).",
        bullets: [
            "Lien de parrainage unique par agence",
            "Récompenses crédit ou mois offerts",
            "Dashboard de suivi des parrainages",
        ],
    },
];

export default function RoadmapPage() {
    const [showDemoModal, setShowDemoModal] = useState(false);
    const openDemoModal = () => setShowDemoModal(true);
    const closeDemoModal = () => setShowDemoModal(false);

    useEffect(() => {
        document.documentElement.classList.add('dark');
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) metaThemeColor.setAttribute('content', '#0a0a1a');
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-[#0a0a1a]">
            <Navbar onDemoClick={openDemoModal} />

            <main className="pt-24 sm:pt-28">
                {/* Hero Roadmap */}
                <section className="relative py-12 md:py-16 overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-[#681793]/10 rounded-full blur-[150px]" />
                        <div className="absolute bottom-0 right-1/4 w-[500px] h-[400px] bg-[#FF914D]/8 rounded-full blur-[150px]" />
                    </div>

                    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Link to="/agences" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors" data-testid="roadmap-back-link">
                            <ArrowLeft className="w-4 h-4" />
                            Retour à l'accueil
                        </Link>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center"
                        >
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#681793]/20 to-[#FF914D]/20 border border-[#FF914D]/30 mb-6">
                                <Sparkles className="w-3.5 h-3.5 text-[#FF914D]" />
                                <span className="text-xs font-bold uppercase tracking-wider text-[#FF914D]">Roadmap Homly</span>
                            </span>

                            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight mb-5">
                                Ce qui est livré,
                                <span className="block bg-gradient-to-r from-[#681793] via-[#9b4dca] to-[#FF914D] bg-clip-text text-transparent">
                                    et ce qui arrive.
                                </span>
                            </h1>

                            <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
                                Notre feuille de route en toute transparence. Homly évolue vite, guidé par les retours terrain des dirigeants d'agence.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Timeline produit existante */}
                <RoadmapSection />

                {/* Bientôt sur Homly */}
                <section className="py-16 md:py-24 bg-gradient-to-b from-slate-950 via-[#0a0a1a] to-slate-950 relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/4 right-0 w-80 h-80 bg-[#681793]/8 rounded-full blur-[120px]" />
                        <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-[#FF914D]/8 rounded-full blur-[120px]" />
                    </div>

                    <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
                            <span className="inline-block px-4 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-xs font-bold uppercase tracking-widest text-white/70 mb-4">
                                Bientôt
                            </span>
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">
                                Les prochains chantiers
                                <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent"> en exploration.</span>
                            </h2>
                            <p className="text-white/55 max-w-xl mx-auto text-sm md:text-base">
                                Ces fonctionnalités ne sont pas encore disponibles. Leur priorité est ajustée selon les retours de nos agences pilotes.
                            </p>
                        </motion.div>

                        <div className="grid md:grid-cols-2 gap-5">
                            {FUTURE_FEATURES.map((f, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.07 }}
                                    className="relative rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 p-6 transition-all"
                                    data-testid={`roadmap-future-${i}`}
                                >
                                    <div className="flex items-start gap-4 mb-3">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793]/40 to-[#FF914D]/30 border border-white/10 flex items-center justify-center shrink-0">
                                            <f.icon className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-lg font-bold text-white">{f.title}</h3>
                                            </div>
                                            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                                f.status === 'exploring'
                                                    ? 'bg-white/[0.05] text-white/40 border border-white/10'
                                                    : 'bg-[#FF914D]/10 text-[#FF914D] border border-[#FF914D]/20'
                                            }`}>
                                                {f.status === 'exploring' ? <Lock className="w-2.5 h-2.5" /> : <Clock className="w-2.5 h-2.5" />}
                                                {f.eta}
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-sm text-white/60 leading-relaxed mb-4">{f.description}</p>

                                    <ul className="space-y-1.5">
                                        {f.bullets.map((b, j) => (
                                            <li key={j} className="flex items-start gap-2 text-xs text-white/50">
                                                <span className="w-1 h-1 rounded-full bg-[#FF914D] mt-1.5 shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="mt-10 text-center"
                        >
                            <p className="text-sm text-white/50 mb-5">
                                Vous avez une fonctionnalité prioritaire à nos yeux ? Parlons-en lors de la démo.
                            </p>
                            <button
                                onClick={openDemoModal}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold shadow-2xl shadow-[#681793]/30 hover:scale-[1.02] transition-transform"
                                data-testid="roadmap-cta-demo"
                            >
                                Réserver ma démo (15 min)
                            </button>
                        </motion.div>
                    </div>
                </section>
            </main>

            <Footer />
            <DemoRequestModal isOpen={showDemoModal} onClose={closeDemoModal} />
        </div>
    );
}
